'use client';

import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { Activity, FC, ReactNode, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { productDetailPayloadToBaseCarData } from '@/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { CarGalleryNavigation } from 'src/constants/constants';
import { Icon } from 'src/general/Icons/Icon';
import { AddToWatchListIcon } from 'src/general/components/AddToWatchList/AddToWatchListIcon';
import { Image } from 'src/general/components/Image/Image';
import { useStatusPillState } from 'src/general/components/StatusPill/useStatusPillState';
import { useUpdatedCarStatus } from 'src/plp/components/Results/useUpdatedCarStatus';
import { useFeatureHotspotsStore } from 'src/stores/featureHotspotsStore';
import { pdpStore } from 'src/stores/pdpStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { productDetailToListDataRow } from 'src/utils/productDetailToListDataRow';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import { DataLayerPdpEvent, pushToDataLayerPDP } from 'src/utils/pushToDataLayerPDP';

import { CarHeroButton } from './CarHeroButton';

const CarRotator = dynamic(() => import('src/pdp/components/CarRotator/CarRotator').then((mod) => mod.CarRotator), {
  ssr: false,
});
const Interior = dynamic(() => import('src/pdp/components/Interior/Interior').then((mod) => mod.Interior), {
  ssr: false,
});

interface Props {
  productDetails: ProductDetailsPayload;
  statusPill: ReactNode;
}

export const CarHero: FC<Props> = ({ productDetails, statusPill }) => {
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );

  const { onClickFeatureHotspot, setViewType } = useFeatureHotspotsStore(
    ({ onClickFeatureHotspot, setViewType }) => ({ onClickFeatureHotspot, setViewType }),
    shallow,
  );

  const baseCarData = productDetailPayloadToBaseCarData(productDetails);
  const carTitle = baseCarData.model;
  const carStatus = useUpdatedCarStatus(productDetails.sku, productDetails.state);
  const [displayMode, setDisplayMode] = useState<CarGalleryNavigation>('exterior');
  const [interiorDisplayedFirstTime, setInteriorDisplayedFirstTime] = useState<boolean>(false);
  const { status } = useStatusPillState({ sku: productDetails.sku, state: carStatus });
  const showClose360Doors = () => {
    const option = displayMode === 'interior' ? 'EXTERIOR' : 'CLOSE_DOOR';
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option,
    });
    setDisplayMode('exterior');
  };

  const showOpen360Doors = () => {
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'OPEN_DOOR',
    });
    setDisplayMode('open');
  };
  const showInterior = () => {
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'INTERIOR',
    });
    setInteriorDisplayedFirstTime(true);
    setDisplayMode('interior');
  };

  const onExpand = () => {
    setCurrentModal({
      name: 'gallery',
      initialState: displayMode,
    });

    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'EXPAND',
    });
  };

  const handleFeatureHotspotClick = (featureName: string, section: 'exterior' | 'interior') => {
    setViewType(section);
    onClickFeatureHotspot(featureName);
    setCurrentModal({
      name: 'gallery',
      initialState: section,
    });
    pushToDataLayer({
      event: 'pdp_cta_interaction',
      value: {
        option: 'HOTSPOT',
        type: 'CLICK',
        value: featureName,
      },
    });
  };

  return (
    <section className="relative aspect-video overflow-hidden rounded-lg bg-(image:--gradient-product-placeholder)">
      <div className="absolute top-0 left-0 h-full w-full">
        <div
          className={clsx('absolute top-0 left-0 h-full w-full', {
            ['hidden']: displayMode !== 'interior',
          })}
        >
          {interiorDisplayedFirstTime && (
            <Interior
              image={productDetails.images.interior[0].url}
              hotspots={productDetails.hotspots?.interior ?? []}
              onClickHotspot={(featureName) => handleFeatureHotspotClick(featureName, 'interior')}
            />
          )}
        </div>

        <Activity mode={displayMode === 'exterior' || displayMode === 'open' ? 'visible' : 'hidden'}>
          <Image
            src={productDetails.images['360doorsclosed'][0].url}
            className="absolute top-0 left-0 h-full w-full object-cover"
            width={358}
            height={201}
            alt={carTitle}
            priority
            data-testid="hero-car-image"
          />
          <CarRotator
            visible
            imagesData={productDetails.images}
            displayMode={displayMode === 'open' ? 'open' : 'exterior'}
            hotspots={productDetails.hotspots?.exterior ?? []}
            onClickHotspot={(featureName, _) => {
              handleFeatureHotspotClick(featureName, 'exterior');
            }}
            hasStatus={!!status}
            alt={carTitle}
          />
        </Activity>
      </div>

      {/* Top left buttons */}
      <div className="absolute top-2 left-2">
        <CarHeroButton icon={<Icon name="expand" />} title="Expand" onClick={onExpand} dataTestId="carHero-expand" />
      </div>
      {/* Top right buttons */}
      <div className="absolute top-2 right-2">
        <AddToWatchListIcon
          data-testid="save-icon"
          sku={productDetails.sku}
          product={productDetailToListDataRow(productDetails)}
        />
      </div>
      {/* Bottom left buttons */}
      <div className="absolute bottom-2 left-2">
        {displayMode === 'exterior' && (
          <CarHeroButton
            icon={<Icon name="open-doors" />}
            title="Open doors"
            onClick={showOpen360Doors}
            dataTestId="carHero-doorsOpen"
            label="Open doors"
          />
        )}
        {displayMode === 'open' && (
          <CarHeroButton
            icon={<Icon name="close-doors" />}
            title="Closed doors"
            onClick={showClose360Doors}
            dataTestId="carHero-doorsClosed"
            label="Exterior"
          />
        )}
      </div>
      {/* Bottom right buttons */}
      <div className="absolute right-2 bottom-2">
        {displayMode === 'interior' && (
          <CarHeroButton
            icon={<Icon name="exterior" />}
            title="Exterior"
            onClick={showClose360Doors}
            dataTestId="carHero-doorsClosed"
            label="Exterior"
            reverseLabel
          />
        )}
        {(displayMode === 'exterior' || displayMode === 'open') && (
          <CarHeroButton
            icon={<Icon name="interior" />}
            title="Interior"
            onClick={showInterior}
            label="Interior"
            dataTestId="carHero-interior"
            reverseLabel
          />
        )}
      </div>

      <div className="absolute top-2 left-1/2 -translate-x-1/2" data-testid="purchase-status-pill-text">
        {statusPill}
      </div>
    </section>
  );
};
